import React, { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../../features/authentication/store/authStore';
import { useContextManager } from '../../hooks/useContextManager';
import { Box, CircularProgress } from '@mui/material';

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Auth provider that initializes authentication state on app startup
 * Checks for existing Cognito session and loads user data if authenticated
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const { initializeAuth, hasHydrated } = useAuthStore();
  const initializationAttempted = useRef(false);

  // Initialize context manager to sync tenant context
  useContextManager();

  useEffect(() => {
    // Prevent double initialization from React StrictMode
    if (initializationAttempted.current) {
      return;
    }

    const initAuth = async () => {
      initializationAttempted.current = true;

      try {
        // Wait for Zustand to hydrate from localStorage
        if (!hasHydrated()) {
          // Wait for hydration - check every 10ms
          const maxWaitTime = 2000; // 2 seconds max wait
          const startTime = Date.now();

          while (!hasHydrated() && Date.now() - startTime < maxWaitTime) {
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        }

        await initializeAuth();
      } catch (error) {
        console.error('AuthProvider: Failed to initialize auth:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();
  }, [initializeAuth, hasHydrated]); // Include dependencies but use ref to prevent double execution

  // Show loading spinner while checking authentication status
  if (isInitializing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};
